/**
 * Money, in one place.
 *
 * The platform prices and reports in euro, so every amount on screen goes
 * through here rather than carrying its own symbol. Changing the currency is
 * then one edit instead of a hunt.
 */

export const CURRENCY = 'EUR';
export const CURRENCY_SYMBOL = '€';

/** An amount from the API, which may arrive as a number or a numeric string. */
export const toAmount = (value: unknown): number => {
  const parsed = typeof value === 'string' ? Number.parseFloat(value) : Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * An amount as people read it: `€412.50`, or `€12.4k` once the exact cents
 * stop mattering more than the size of the number.
 */
export function money(value: unknown, { short = false, decimals = 2 }: { short?: boolean; decimals?: number } = {}) {
  const amount = toAmount(value);
  if (short && Math.abs(amount) >= 1000) {
    const thousands = amount / 1000;
    return `${CURRENCY_SYMBOL}${thousands.toFixed(Math.abs(amount) >= 10_000 ? 0 : 1)}k`;
  }
  return `${CURRENCY_SYMBOL}${amount.toFixed(decimals)}`;
}

/** Usage costs can be below a cent; keep two significant digits in that range. */
export function costMoney(value: unknown) {
  const amount = toAmount(value);
  const magnitude = Math.abs(amount);
  const decimals = magnitude > 0 && magnitude < 0.01 ? Math.ceil(-Math.log10(magnitude)) + 1 : 2;
  return decimals > 20 ? `${CURRENCY_SYMBOL}${amount.toExponential(2)}` : money(amount, { decimals });
}

/** A price beside a period, e.g. `€49.00/mo`. Empty values stay a dash. */
export function price(value: unknown, suffix = '') {
  if (value === undefined || value === null || value === '') return '—';
  return `${money(value)}${suffix}`;
}
